import "./PdfDocumentView.css";
import { Loader } from "@ui/Loader";
import type { PDFDocumentLoadingTask } from "pdfjs-dist";
import type { PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import {
	createEffect,
	createMemo,
	createSignal,
	on,
	onCleanup,
	onMount,
	Show,
} from "solid-js";

export type PdfDocumentViewHandle = {
	/**
	 * Scrolls a page to the top of the view. {@link Props.page} does the same
	 * when it changes; this is for asking for a page that is already the one
	 * asked for, which the reader has since scrolled away from.
	 */
	goToPage: (page: number) => void;
};

type Props = {
	/**
	 * PDF URL signed for inline display. A URL signed for download carries
	 * `Content-Disposition: attachment`, which browsers save rather than render.
	 */
	url: string;
	/** 1-based page to open at, and to scroll to whenever it changes. */
	page: number;
	/** CSS length for the height. The width always fills the container. */
	height: string;
	/** Stands in where the document cannot be drawn. */
	fallback?: { url: string; alt: string };
	/** Accessible name, e.g. `"Quarterly report, 12 pages"`. */
	label: string;
	/** Called with the page scrolling brings into view. */
	onPageChange?: (page: number) => void;
	handle?: (handle: PdfDocumentViewHandle) => void;
};

type Pdfjs = Awaited<ReturnType<typeof import("./pdfjs").loadPdfjs>>;

let loading: Promise<Pdfjs> | undefined;

/** Loads pdf.js on first use and shares it with every later viewer. */
const pdfjs = () => (loading ??= import("./pdfjs").then((m) => m.loadPdfjs()));

/** Dragging a pane edge re-scales the pages once it settles, not on every frame. */
const REFIT_SETTLE_MS = 120;

/**
 * How long a document may take to appear before the wait is worth showing.
 * Below this the spinner would flash rather than inform, which a cached pdf.js
 * and a small document otherwise do every time.
 */
const SPINNER_AFTER_MS = 200;

const prefersReducedMotion = () =>
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A whole PDF, drawn by pdf.js: its pages stacked down a box the reader scrolls
 * through, each scaled to the full width of the container. A container that
 * changes width re-scales the pages and keeps the reader's place.
 *
 * Opening at {@link Props.page}, and scrolling to it whenever it changes, is
 * what wires the viewer to a page thumbnail strip beside it. Scrolling reports
 * the page reached through {@link Props.onPageChange}.
 *
 * Drawing the document ourselves is what makes any of that possible: the
 * viewers built into browsers each fit, scroll and decorate a PDF their own
 * way, none of them can be asked for a page, and WebKit's keeps whatever scale
 * it first loaded at. Where a document cannot be drawn at all,
 * {@link Props.fallback} takes its place.
 *
 * It costs a wait, though: pdf.js is a chunk of its own, fetched the first time
 * a reader opens a document, and the document follows it. A spinner covers both
 * once the wait is long enough to be worth showing.
 */
export const PdfDocumentView = (props: Props) => {
	const [hasFailed, setHasFailed] = createSignal(false);
	const [isWaiting, setIsWaiting] = createSignal(false);

	let container!: HTMLDivElement;
	let pages!: HTMLDivElement;

	let viewer: PDFViewer | undefined;
	let loadingTask: PDFDocumentLoadingTask | undefined;
	/** Everything pdf.js listens to, and everything we start, ends with this. */
	const teardown = new AbortController();
	let settleTimer: number | undefined;
	let spinnerTimer: number | undefined;
	let containerWidth = 0;
	/** True between the document's pages being laid out and the next document. */
	let isLaidOut = false;
	/** Asked for before the pages existed, and scrolled to once they do. */
	let pendingPage: number | undefined;

	/*
	 * A document keeps the reader waiting twice over: pdf.js itself is fetched
	 * on first use, which is the longer wait of the two, and then the document
	 * is fetched from storage. Both are covered from the moment a URL is asked
	 * for until a page of it has been drawn.
	 */
	const startWaiting = () => {
		window.clearTimeout(spinnerTimer);
		spinnerTimer = window.setTimeout(
			() => setIsWaiting(true),
			SPINNER_AFTER_MS,
		);
	};

	const stopWaiting = () => {
		window.clearTimeout(spinnerTimer);
		setIsWaiting(false);
	};

	/**
	 * Scrolls a page to the top of the view, travelling there rather than
	 * arriving, so that the pages in between show how far the reader has moved
	 * through the document. A page opened at is `"instant"`: it is where the
	 * reading starts, not somewhere to be taken.
	 */
	const goToPage = (page: number, behavior: ScrollBehavior = "smooth") => {
		if (viewer === undefined || !isLaidOut) {
			pendingPage = page;
			return;
		}
		pendingPage = undefined;
		const pageNumber = Math.min(Math.max(page, 1), viewer.pagesCount);
		const sheet: HTMLElement | undefined = viewer.getPageView(
			pageNumber - 1,
		)?.div;
		if (
			sheet === undefined ||
			behavior === "instant" ||
			prefersReducedMotion()
		) {
			viewer.scrollPageIntoView({ pageNumber });
			return;
		}
		// Pages are positioned within the scroller, so this is the distance to
		// scroll it by - the same sum pdf.js arrives at, animated.
		container.scrollTo({ top: sheet.offsetTop + sheet.clientTop, behavior });
	};

	/**
	 * Scales the pages to the width of the container. pdf.js holds the page the
	 * reader is on, and how far down it they are, across the change.
	 */
	const refit = () => {
		if (viewer !== undefined && container.clientWidth > 0) {
			viewer.currentScaleValue = "page-width";
		}
	};

	const createViewer = ({
		AnnotationEditorType,
		AnnotationMode,
		EventBus,
		PDFViewer: Viewer,
	}: Pdfjs) => {
		const eventBus = new EventBus();
		// The published options leave `abortSignal` out; it is what releases the
		// viewer's own scroll and resize listeners.
		const options: ConstructorParameters<typeof Viewer>[0] & {
			abortSignal: AbortSignal;
		} = {
			container,
			viewer: pages,
			eventBus,
			abortSignal: teardown.signal,
			// Pages fill the container, and the gap between them is left to CSS.
			removePageBorders: true,
			// Forms, links and the editing tools belong to a full viewer; this one
			// draws the pages and the text over them, and nothing besides.
			annotationMode: AnnotationMode.DISABLE,
			annotationEditorMode: AnnotationEditorType.DISABLE,
			enableAutoLinking: false,
			// Leaves selection highlighting to the browser and the text layer.
			enableSelectionRendering: false,
		};
		const created = new Viewer(options);

		eventBus.on(
			"pagesinit",
			() => {
				isLaidOut = true;
				refit();
				if (pendingPage !== undefined) {
					goToPage(pendingPage, "instant");
				}
			},
			{ signal: teardown.signal },
		);
		eventBus.on(
			"pagechanging",
			({ pageNumber }: { pageNumber: number }) =>
				props.onPageChange?.(pageNumber),
			{ signal: teardown.signal },
		);
		// Laid out is not yet drawn: the wait ends with the first page a reader
		// can actually see, not with the blank sheet it is drawn on.
		eventBus.on("pagerendered", stopWaiting, { signal: teardown.signal });

		return created;
	};

	const open = async (url: string) => {
		const api = await pdfjs();
		if (teardown.signal.aborted) {
			return;
		}
		viewer ??= createViewer(api);
		const pdfViewer = viewer;

		const previous = loadingTask;
		const task = api.getDocument({ url });
		loadingTask = task;
		void previous?.destroy();

		try {
			const pdf = await task.promise;
			if (loadingTask === task && !teardown.signal.aborted) {
				pdfViewer.setDocument(pdf);
			}
		} catch {
			// A document that has been replaced, or torn down, under us is not the
			// one the reader is waiting for.
			if (loadingTask === task && !teardown.signal.aborted) {
				setHasFailed(true);
				stopWaiting();
			}
		}
	};

	onMount(() => {
		props.handle?.({ goToPage });

		const observer = new ResizeObserver((entries) => {
			const measured = Math.round(entries[0]?.contentRect.width ?? 0);
			if (measured === 0 || measured === containerWidth) {
				return;
			}
			// The first width is the one the document opens at, and is worth no
			// wait; later ones come from a drag or a pane sliding open, which
			// arrive by the frame.
			const isFirstWidth = containerWidth === 0;
			containerWidth = measured;
			if (isFirstWidth) {
				refit();
				return;
			}
			window.clearTimeout(settleTimer);
			settleTimer = window.setTimeout(refit, REFIT_SETTLE_MS);
		});
		observer.observe(container);

		onCleanup(() => {
			observer.disconnect();
			window.clearTimeout(settleTimer);
			window.clearTimeout(spinnerTimer);
			teardown.abort();
			viewer?.cleanup();
			void loadingTask?.destroy();
		});
	});

	/*
	 * Both of these read through a memo rather than straight off the props: a
	 * caller handing us a fresh object each time - which the pane does, once per
	 * page the reader turns to - would otherwise re-run the effect on every one
	 * of them, and reload a document that has not changed.
	 */
	const url = createMemo(() => props.url);
	const page = createMemo(() => props.page);

	createEffect(
		on(url, (value) => {
			isLaidOut = false;
			pendingPage = page();
			setHasFailed(false);
			startWaiting();
			void open(value);
		}),
	);

	createEffect(on(page, (value) => goToPage(value), { defer: true }));

	return (
		<section
			class="gvtui-pdf-document-view"
			classList={{ "gvtui-pdf-document-view--failed": hasFailed() }}
			aria-label={props.label}
			aria-busy={isWaiting()}
			style={{ "--pdf-document-view-height": props.height }}
		>
			<div
				class="gvtui-pdf-document-view__scroller"
				// biome-ignore lint/a11y/noNoninteractiveTabindex: the document scrolls inside this box, which only reaches the keyboard if the box can be focused.
				tabIndex={0}
				ref={container}
			>
				<div class="pdfViewer" ref={pages} />
			</div>
			<Show when={isWaiting()}>
				<div
					class="gvtui-pdf-document-view__waiting"
					role="status"
					aria-label="Loading the document"
				>
					<Loader />
				</div>
			</Show>
			<Show when={hasFailed() ? props.fallback : undefined}>
				{(fallback) => (
					<img
						class="gvtui-pdf-document-view__fallback"
						src={fallback().url}
						alt={fallback().alt}
					/>
				)}
			</Show>
		</section>
	);
};
